import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUnifiedSubscription } from '../contexts/SubscriptionContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Store, 
  Package, 
  Activity, 
  TrendingUp, 
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
  DollarSign,
  ArrowRight,
  Plus,
  RefreshCw,
  Eye,
  BarChart3,
  Gauge,
  XCircle
} from 'lucide-react';
import { storeService, RecentSyncJob, DashboardStats as ServiceDashboardStats } from '../services/store';
import { useOrganization } from '../contexts/OrganizationContext';

interface DashboardStats {
  totalStores: number;
  connectedStores: number;
  totalProducts: number;
  syncedProducts: number;
  activeSyncJobs: number;
  recentSyncJobs: RecentSyncJob[];
  lastSyncDate?: string;
  storeStats: Array<{
    id?: string;
    name: string;
    status: string;
    productCount: number;
    lastSync?: string;
  }>;
}

interface QuickMetric {
  title: string;
  value: string | number;
  change?: {
    value: number | string;
    trend: 'up' | 'down' | 'neutral';
    label: string;
    iconType?: 'trend' | 'time' | 'gauge' | 'activity';
  };
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'destructive';
  href?: string;
}

// Helper function to format relative time
const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  if (diffInMinutes < 1) {
    return 'just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { subscription, usageDetails } = useUnifiedSubscription();
  const { hasPermission } = useOrganization();
  const [stats, setStats] = useState<DashboardStats>({
    totalStores: 0,
    connectedStores: 0,
    totalProducts: 0,
    syncedProducts: 0,
    activeSyncJobs: 0,
    recentSyncJobs: [],
    storeStats: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshingSyncJobs, setIsRefreshingSyncJobs] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // PERFORMANCE FIX: Single API call instead of N+1 queries
      // Previously: 1 stores call + 1 products call + N store product count calls + 1 sync jobs call
      // Now: 1 dashboard-stats call that returns everything
      const dashboardStats = await storeService.getDashboardStats();

      // Format the last sync date for display
      const lastSyncDate = dashboardStats.lastSyncDate
        ? formatRelativeTime(new Date(dashboardStats.lastSyncDate))
        : undefined;

      setStats({
        totalStores: dashboardStats.totalStores,
        connectedStores: dashboardStats.connectedStores,
        totalProducts: dashboardStats.totalProducts,
        syncedProducts: 0, // Not tracked in new endpoint, can add if needed
        activeSyncJobs: dashboardStats.activeSyncJobs,
        recentSyncJobs: dashboardStats.recentSyncJobs,
        lastSyncDate,
        storeStats: dashboardStats.storeStats
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSyncJobs = async () => {
    setIsRefreshingSyncJobs(true);
    try {
      const recentSyncJobs = await storeService.getRecentSyncJobs(5);
      setStats(prevStats => ({
        ...prevStats,
        recentSyncJobs
      }));
    } catch (error) {
      console.warn('Failed to refresh sync jobs:', error);
    } finally {
      setIsRefreshingSyncJobs(false);
    }
  };

  const quickMetrics: QuickMetric[] = [
    {
      title: 'Connected Stores',
      value: `${stats.connectedStores}/${stats.totalStores}`,
      change: stats.totalStores > 0 ? {
        value: Math.round((stats.connectedStores / stats.totalStores) * 100),
        trend: stats.connectedStores === stats.totalStores ? 'up' : 'neutral',
        label: 'connection rate'
      } : undefined,
      icon: <Store className="h-5 w-5" />,
      color: 'primary',
      href: '/stores'
    },
    {
      title: 'Total Products',
      value: stats.totalProducts.toLocaleString(),
      change: stats.lastSyncDate ? {
        value: stats.lastSyncDate,
        trend: 'neutral',
        label: 'last sync',
        iconType: 'time'
      } : undefined,
      icon: <Package className="h-5 w-5" />,
      color: 'success',
      href: '/products'
    },
    {
      title: 'Active Syncs',
      value: stats.activeSyncJobs,
      change: stats.activeSyncJobs > 0 ? {
        value: stats.activeSyncJobs,
        trend: 'neutral',
        label: 'syncing now',
        iconType: 'activity'
      } : undefined,
      icon: <Activity className="h-5 w-5" />,
      color: stats.activeSyncJobs > 0 ? 'warning' : 'success',
      href: '/stores'
    },
    {
      title: 'Usage',
      value: `${usageDetails?.credits?.percentage?.toFixed(0) || 0}%`,
      change: {
        value: usageDetails?.credits?.percentage || 0,
        trend: (usageDetails?.credits?.percentage || 0) > 80 ? 'up' : 'neutral',
        label: 'of limit',
        iconType: 'gauge'
      },
      icon: <BarChart3 className="h-5 w-5" />,
      color: (usageDetails?.credits?.percentage || 0) > 90 ? 'destructive' : 'primary',
      href: '/billing'
    }
  ];

  const getMetricColor = (color: QuickMetric['color']) => {
    switch (color) {
      case 'primary': return 'text-primary';
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      case 'destructive': return 'text-destructive';
      default: return 'text-primary';
    }
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral', iconType: 'trend' | 'time' | 'gauge' | 'activity' = 'trend') => {
    if (trend === 'up') {
      return <TrendingUp className="h-3 w-3 text-success" />;
    }
    if (trend === 'down') {
      return <TrendingDown className="h-3 w-3 text-destructive" />;
    }
    
    // For neutral trend, show contextual icon based on type
    switch (iconType) {
      case 'time': return <Clock className="h-3 w-3 text-muted-foreground" />;
      case 'gauge': return <Gauge className="h-3 w-3 text-muted-foreground" />;
      case 'activity': return <Activity className="h-3 w-3 text-muted-foreground" />;
      default: return <BarChart3 className="h-3 w-3 text-muted-foreground" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="h-4 w-4 text-success" />;
      case 'syncing': return <RefreshCw className="h-4 w-4 text-warning animate-spin" />;
      case 'error': return <AlertCircle className="h-4 w-4 text-destructive" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected': return <Badge variant="outline" className="text-success border-success/20 bg-success/10">Connected</Badge>;
      case 'syncing': return <Badge variant="outline" className="text-warning border-warning/20 bg-warning/10">Syncing</Badge>;
      case 'error': return <Badge variant="destructive">Error</Badge>;
      default: return <Badge variant="secondary">Disconnected</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-16 bg-muted rounded-xl"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-xl"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-96 bg-muted rounded-xl"></div>
          <div className="h-96 bg-muted rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between animate-slide-down">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.email?.split('@')[0] || 'User'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={loadDashboardData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch animate-slide-up">
        {quickMetrics.map((metric, index) => (
          <Card key={index} className="card-enhanced group hover:shadow-lg transition-all duration-200">
            <CardContent className="p-6 h-full flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    {metric.title}
                  </p>
                  <p className="text-2xl font-bold">
                    {metric.value}
                  </p>
                  {/* Reserve space for change indicator - always show this div */}
                  <div className="flex items-center gap-1 text-xs min-h-[16px]">
                    {metric.change ? (
                      <>
                        {getTrendIcon(metric.change.trend, metric.change.iconType)}
                        <span className={metric.change.trend === 'up' ? 'text-success' : metric.change.trend === 'down' ? 'text-destructive' : 'text-muted-foreground'}>
                          {typeof metric.change.value === 'number' ? `${metric.change.value}%` : metric.change.value} {metric.change.label}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </div>
                </div>
                <div className={`p-3 rounded-lg bg-gradient-to-br from-background to-muted/50 ${getMetricColor(metric.color)} shrink-0`}>
                  {metric.icon}
                </div>
              </div>
              
              {/* Always show the link area for consistent height */}
              <div className="mt-auto pt-2">
                {metric.href ? (
                  <Link
                    to={metric.href}
                    className="inline-flex items-center text-xs text-primary hover:text-primary-hover group-hover:translate-x-1 transition-all"
                  >
                    View details <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                ) : (
                  <div className="h-4"></div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up" style={{animationDelay: '100ms'}}>
        {/* Recent Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Store Overview */}
          <Card className="card-enhanced">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="text-lg">Store Overview</CardTitle>
                <CardDescription>
                  Connected stores and sync status
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/stores" className="gap-2">
                  <Eye className="h-4 w-4" />
                  View All
                </Link>
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {stats.storeStats.length > 0 ? (
                stats.storeStats.map((store, index) => (
                  <div key={index} className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(store.status)}
                      <div>
                        <p className="font-medium">{store.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {store.productCount} products
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(store.status)}
                      {store.lastSync && (
                        <p className="text-xs text-muted-foreground">
                          Last sync: {new Date(store.lastSync).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Store className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No stores connected yet</p>
                  <Button className="mt-4" asChild>
                    <Link to="/stores">Connect Your First Store</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Sync Jobs */}
          <Card className="card-enhanced">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="text-lg">Recent Sync Jobs</CardTitle>
                <CardDescription>
                  Latest product synchronization activity
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={refreshSyncJobs}
                  disabled={isRefreshingSyncJobs}
                  className="gap-2"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshingSyncJobs ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/jobs" className="gap-2">
                    <Activity className="h-4 w-4" />
                    View All
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {stats.recentSyncJobs.length > 0 ? (
                stats.recentSyncJobs.map((job, index) => {
                  const getStatusIcon = () => {
                    switch (job.status) {
                      case 'in_progress':
                        return <RefreshCw className="h-4 w-4 text-warning animate-spin" />;
                      case 'completed':
                        return <CheckCircle className="h-4 w-4 text-success" />;
                      case 'failed':
                        return <AlertCircle className="h-4 w-4 text-destructive" />;
                      case 'cancelled':
                        return <XCircle className="h-4 w-4 text-muted-foreground" />;
                      default:
                        return <Clock className="h-4 w-4 text-muted-foreground" />;
                    }
                  };

                  const getStatusBadgeVariant = () => {
                    // Color based on progress percentage, not just status
                    if (job.progress === 100) {
                      return 'outline'; // Green outline for 100% completion
                    } else if (job.status === 'failed') {
                      return 'destructive'; // Red for failed
                    } else if (job.status === 'in_progress') {
                      return 'secondary'; // Gray for in progress
                    } else {
                      return 'secondary'; // Default gray for partial completion
                    }
                  };

                  const getProgressBadgeClass = () => {
                    if (job.progress === 100) {
                      return 'text-success border-success/20 bg-success/10';
                    } else if (job.progress >= 50) {
                      return 'text-warning border-warning/20 bg-warning/10';
                    } else {
                      return 'text-muted-foreground border-border bg-muted/50';
                    }
                  };

                  return (
                    <div key={job.id} className="space-y-3 p-4 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon()}
                          <div>
                            <p className="font-medium">{job.store_name}</p>
                            <p className="text-sm text-muted-foreground capitalize">
                              {job.status.replace('_', ' ')}
                              {job.total_products > 0 && (
                                <span className="ml-2">• {job.processed_products.toLocaleString()}/{job.total_products.toLocaleString()} products</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={getProgressBadgeClass()}>
                            {job.progress}%
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(new Date(job.created_at))}
                          </span>
                        </div>
                      </div>
                      {job.status === 'in_progress' && (
                        <Progress value={job.progress} className="h-2" />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-1">No recent sync activity</p>
                  <p className="text-xs text-muted-foreground mb-4">
                    Sync jobs will appear here when you sync your stores
                  </p>
                  {stats.connectedStores > 0 ? (
                    <Button className="mt-4" asChild>
                      <Link to="/stores">Sync Your Stores</Link>
                    </Button>
                  ) : (
                    <Button className="mt-4" asChild>
                      <Link to="/stores">Connect a Store First</Link>
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Usage Overview */}
          {subscription && (
            <Card className="card-enhanced">
              <CardHeader>
                <CardTitle className="text-lg">Usage Overview</CardTitle>
                <CardDescription>
                  Current plan and usage statistics
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{subscription.plan_name}</span>
                  <Badge variant="outline" className="text-primary border-primary/20 bg-primary/10">
                    {subscription.status}
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Usage</span>
                    <span className="font-medium">
                      {usageDetails?.credits?.percentage?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <Progress 
                    value={usageDetails?.credits?.percentage || 0} 
                    className="h-3"
                  />
                  <p className="text-xs text-muted-foreground">
                    {usageDetails?.credits?.remaining || 0} of {usageDetails?.credits?.limit || 0} remaining
                  </p>
                </div>

                <div className="pt-4 border-t border-border">
                  <Button variant="outline" size="sm" className="w-full gap-2" asChild>
                    <Link to="/billing">
                      <DollarSign className="h-4 w-4" />
                      Manage Billing
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="card-enhanced animate-slide-left">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <div className="p-1.5 bg-primary/10 rounded-lg animate-pulse-subtle">
                  <Zap className="h-4 w-4 text-primary" />
                </div>
                Quick Actions
              </CardTitle>
              <CardDescription>
                Common tasks and shortcuts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {stats.totalStores === 0 ? (
                <Button variant="outline" size="sm" className="w-full justify-start gap-3 hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 group" asChild>
                  <Link to="/stores">
                    <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-300" />
                    Connect Your First Store
                    <ArrowRight className="h-3 w-3 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" className="w-full justify-start gap-3 hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 group" asChild>
                    <Link to="/stores">
                      <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-300" />
                      Add Another Store
                      <ArrowRight className="h-3 w-3 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start gap-3 hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 group" asChild>
                    <Link to="/products">
                      <Package className="h-4 w-4 group-hover:animate-bounce" />
                      Browse Products
                      <ArrowRight className="h-3 w-3 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  </Button>
                  {stats.connectedStores > 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start gap-3 hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 group"
                      onClick={() => {
                        // Trigger sync for the first connected store
                        const connectedStore = stats.storeStats.find(s => s.status === 'connected');
                        if (connectedStore) {
                          // This would trigger a sync - for now just navigate to stores
                          window.location.href = '/stores';
                        }
                      }}
                    >
                      <RefreshCw className="h-4 w-4 group-hover:animate-spin" />
                      Sync Products
                      <ArrowRight className="h-3 w-3 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};